import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Circle, MapPin, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { apiClient, UserListItem } from '../api/client';
import { AppBadge, AppFilterSelect, AppFilterValue } from '../components/AppFlavor';

export default function UserList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [platform, setPlatform] = useState('');
  const [app, setApp] = useState<AppFilterValue>('');
  const [teamName, setTeamName] = useState(searchParams.get('team') || '');
  const [teams, setTeams] = useState<string[]>([]);

  // Team autocomplete state
  const [teamInput, setTeamInput] = useState(searchParams.get('team') || '');
  const [showTeamSuggestions, setShowTeamSuggestions] = useState(false);
  const teamInputRef = useRef<HTMLInputElement>(null);
  const teamDropdownRef = useRef<HTMLDivElement>(null);

  // Filter teams based on input
  const filteredTeams = teams.filter(team =>
    team.toLowerCase().includes(teamInput.toLowerCase())
  );

  // Fetch teams for filter
  useEffect(() => {
    async function fetchTeams() {
      try {
        const data = await apiClient.getTeams();
        setTeams(data.teams);
      } catch (err) {
        console.error('Failed to load teams:', err);
      }
    }
    fetchTeams();
  }, []);

  // Handle URL param changes
  useEffect(() => {
    const teamFromUrl = searchParams.get('team');
    if (teamFromUrl) {
      setTeamName(teamFromUrl);
      setTeamInput(teamFromUrl);
    }
  }, [searchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        teamDropdownRef.current &&
        !teamDropdownRef.current.contains(event.target as Node) &&
        teamInputRef.current &&
        !teamInputRef.current.contains(event.target as Node)
      ) {
        setShowTeamSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      try {
        const data = await apiClient.getUsers({
          search: search || undefined,
          status: status || undefined,
          platform: platform || undefined,
          app: app || undefined,
          team_name: teamName || undefined,
          page,
          limit: 20,
        });
        setUsers(data.users);
        setTotal(data.total);
        setPages(data.pages);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setLoading(false);
      }
    }

    const debounce = setTimeout(fetchUsers, 300);
    return () => clearTimeout(debounce);
  }, [search, status, platform, app, teamName, page]);

  const handleTeamSelect = (team: string) => {
    setTeamName(team);
    setTeamInput(team);
    setShowTeamSuggestions(false);
    // Update URL
    if (team) {
      setSearchParams({ team });
    } else {
      setSearchParams({});
    }
  };

  const clearTeamFilter = () => {
    setTeamName('');
    setTeamInput('');
    setSearchParams({});
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <span className="text-sm text-gray-500">{total} total users</span>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex gap-4 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by email, driver ID, or device ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="active">Online</option>
            <option value="inactive">Offline</option>
            <option value="crashed">Had Crashes</option>
          </select>

          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Platforms</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
          </select>

          <AppFilterSelect
            value={app}
            onChange={(value) => {
              setApp(value);
              setPage(1);
            }}
          />

          {/* Team filter with autocomplete */}
          <div className="relative">
            <input
              ref={teamInputRef}
              type="text"
              placeholder="Filter by team..."
              value={teamInput}
              onChange={(e) => {
                setTeamInput(e.target.value);
                setShowTeamSuggestions(true);
                // Clear the actual filter if input is cleared
                if (!e.target.value) {
                  setTeamName('');
                  setSearchParams({});
                }
              }}
              onFocus={() => setShowTeamSuggestions(true)}
              className="w-48 px-4 py-2 pr-8 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {teamName && (
              <button
                onClick={clearTeamFilter}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
            {showTeamSuggestions && filteredTeams.length > 0 && (
              <div
                ref={teamDropdownRef}
                className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
              >
                {filteredTeams.map((team) => (
                  <button
                    key={team}
                    onClick={() => handleTeamSelect(team)}
                    className={`w-full px-4 py-2 text-left text-gray-900 hover:bg-gray-100 ${
                      team === teamName ? 'bg-primary-50 text-primary-600' : ''
                    }`}
                  >
                    {team}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Active team filter badge */}
        {teamName && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm text-gray-500">Filtering by team:</span>
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
              {teamName}
              <button onClick={clearTeamFilter} className="hover:text-primary-900">
                <X size={14} />
              </button>
            </span>
          </div>
        )}
      </div>

      {/* User table */}
      {loading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Device
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Version
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last Seen
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Crashes
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.device_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link
                        to={`/users/${user.device_id}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        <div className="font-medium">
                          {user.email || user.driver_id || 'Not Logged In'}
                        </div>
                        {user.team_name && (
                          <div className="text-xs text-gray-600">{user.team_name}</div>
                        )}
                        <div className="text-xs text-gray-500">{user.device_id.slice(0, 8)}...</div>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`badge ${
                            user.platform === 'ios' ? 'badge-info' : 'badge-success'
                          }`}
                        >
                          {user.platform === 'ios' ? 'iOS' : 'Android'}
                        </span>
                        <AppBadge flavor={user.app_flavor} />
                        <span className="text-sm text-gray-600">{user.device_model}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900">
                        v{user.app_version}
                        {user.build_number && <span className="text-gray-500"> ({user.build_number})</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Circle
                          size={10}
                          className={user.is_online ? 'text-green-500 fill-green-500' : 'text-gray-300 fill-gray-300'}
                        />
                        <span className="text-sm text-gray-600">
                          {user.is_online ? 'Online' : 'Offline'}
                        </span>
                        {user.is_moving && (
                          <MapPin size={14} className="text-blue-500" aria-label="Moving" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {formatDistanceToNow(new Date(user.last_seen), { addSuffix: true })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {user.recent_crashes > 0 ? (
                        <span className="badge badge-error">{user.recent_crashes}</span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-gray-500">
              Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total} users
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {pages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page === pages}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
